import { AnalyzerConfig, Finding } from '../types/contractAnalyzer';

interface RuleContext {
  text: string;
  config: AnalyzerConfig;
}

type RuleFn = (ctx: RuleContext) => Finding[];

// Simple regex-based rules for MVP. Will expand per jurisdiction.
const ruleSoleDiscretion: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  const regex = /sole\s+discretion/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const start = Math.max(0, match.index - 80);
    const end = Math.min(text.length, match.index + 120);
    findings.push({
      id: `sole-discretion-${match.index}`,
      category: 'Definitions',
      severity: 'medium',
      actionType: 'cta',
      title: 'Unilateral "sole discretion" language',
      summary: 'Language granting a party rights in its "sole discretion" can be one-sided and risky.',
      recommendation: 'Negotiate to replace with objective standards (e.g., "not to be unreasonably withheld"), or add reciprocal rights.',
      clauseSpan: {
        text: text.slice(start, end),
        startIndex: start,
        endIndex: end,
      },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.6, rationale: 'Regex keyword match' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// NEC3: Programme reply periods and revision cadence (added to resolve missing symbol lint)
const ruleNEC3ProgrammeReplyCadence: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const mentionsProgramme = /programme\b/i.test(text);
  if (!mentionsProgramme) return findings;
  const pmReply = /(project\s+manager|PM)\s+(responds|replies)\s+within\s+\d+\s*(?:days|weeks)/i.test(text);
  const cadence = /(revise|update|submit\s+a\s+revised\s+programme)\s+(?:within|every)\s+\d+\s*(?:days|weeks|months)/i.test(text);
  if (!pmReply || !cadence) {
    const idx = text.search(/programme\b/i);
    const s = Math.max(0, idx - 160); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 260);
    findings.push({
      id: 'nec3-programme-reply-cadence-weak',
      category: 'Other',
      severity: 'medium',
      actionType: 'cta',
      title: 'Programme reply period or revision cadence unclear',
      summary: 'Programme section does not clearly set PM reply periods or the revision/update cadence.',
      recommendation: 'Add explicit PM reply periods (e.g., responds within X days) and a clear revision cadence (e.g., submit revised programme every Y weeks).',
      clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.45, rationale: 'Missing tokens for reply periods or cadence' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// NEC3: CE pathway completeness (notice → quotation → PM assessment → implementation)
const ruleNEC3CEPathway: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const hasNotice = /(notify|notification)\s+of\s+(?:compensation\s+event|CE)/i.test(text);
  const hasQuotation = /(submit\s+quotations?|quotation\s+for\s+the\s+CE)/i.test(text);
  const hasAssessment = /(project\s+manager\s+assesses|assessment\s+of\s+the\s+CE)/i.test(text);
  const hasImplement = /(implement|instruction\s+to\s+proceed|implemented\s+CE)/i.test(text);
  const hasAll = hasNotice && hasQuotation && hasAssessment && hasImplement;
  if (!hasAll) {
    const idx = text.search(/compensation\s+event|CE/i);
    const s = Math.max(0, idx - 200); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 320);
    findings.push({
      id: 'nec3-ce-pathway-incomplete',
      category: 'Other',
      severity: 'high',
      actionType: 'cta',
      title: 'Compensation Event pathway not fully defined',
      summary: 'The CE process should cover notification, quotation, PM assessment, and implementation. One or more steps appear unclear or missing.',
      recommendation: 'Define CE notification, quotation submission, PM assessment timelines, and implementation instructions to avoid disputes.',
      clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.5, rationale: 'Missing tokens for one or more CE steps' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleNEC3ConstructionActRights: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const interestMention = /(interest\s+on\s+late\s+payment|late\s+payment\s+interest)/i.test(text);
  const suspensionMention = /(suspend\s+performance|right\s+to\s+suspend)/i.test(text);
  if (!interestMention || !suspensionMention) {
    const idx = text.search(/payment|interest|suspend/i);
    const s = Math.max(0, idx - 160); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 260);
    findings.push({
      id: 'nec3-construction-act-rights-weak',
      category: 'PaymentTerms',
      severity: 'medium',
      actionType: 'cta',
      title: 'Construction Act rights: interest and suspension not explicit',
      summary: 'Late payment interest and the statutory right to suspend for non-payment are not both clearly stated.',
      recommendation: 'Add explicit late payment interest and right to suspend performance for non-payment, consistent with the Construction Act.',
      clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.45, rationale: 'Missing interest/suspension tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleJCTLADsCapsConcurrency: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  // Look for LADs area
  const ladIdx = text.search(/liquidated\s+damages|LADs?|delay\s+damages/i);
  if (ladIdx === -1) return findings;
  const s = Math.max(0, ladIdx - 300); const e = Math.min(text.length, ladIdx + 500);
  const ctx = text.slice(s, e);
  // Caps: overall cap on LADs or aggregate cap
  const hasCap = /(cap|maximum|aggregate\s+limit|overall\s+limit)/i.test(ctx);
  if (!hasCap) {
    findings.push({
      id: 'jct-lads-cap-missing',
      category: 'PaymentTerms',
      severity: 'low',
      actionType: 'draft',
      title: 'LADs overall cap not detected',
      summary: 'Liquidated damages are referenced but no overall cap/maximum is detected; consider whether a cap is intended.',
      recommendation: 'If appropriate, add an overall cap (e.g., a percentage of the Contract Sum) for total LADs across the project/sections.',
      clauseSpan: { text: ctx, startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.35, rationale: 'LADs context without cap tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  // Concurrency: mention of concurrent delay handling
  const hasConcurrency = /(concurrent\s+delay|concurrency)/i.test(ctx);
  if (!hasConcurrency) {
    findings.push({
      id: 'jct-lads-concurrency-stance-unclear',
      category: 'Other',
      severity: 'low',
      actionType: 'draft',
      title: 'Concurrency stance not explicit',
      summary: 'No explicit stance on concurrent delay in the LADs/extension sections; this can affect entitlement and LADs application.',
      recommendation: 'State how concurrent delay is treated (e.g., EOT allowed without loss/expense, or shared risk) per your policy.',
      clauseSpan: { text: ctx, startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.35, rationale: 'No concurrency tokens detected' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleJCTSectionalCompletion: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /sectional\s+completion/i.test(text);
  if (mentions) {
    const hasDatesRates = /(section(?:\s+\d+)?\s+completion\s+date|sectional\s+liquidated\s+damages|per\s+section)/i.test(text);
    if (!hasDatesRates) {
      const idx = text.search(/sectional\s+completion/i);
      const s = Math.max(0, idx - 160); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 260);
      findings.push({
        id: 'jct-sectional-completion-unclear',
        category: 'Other',
        severity: 'medium',
        actionType: 'cta',
        title: 'Sectional completion details unclear',
        summary: 'Sectional completion is mentioned but specific section dates and LADs per section are not clearly set out.',
        recommendation: 'Specify each section’s completion date and associated LADs, and confirm partial possession/defects periods per section.',
        clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.45, rationale: 'Sectional completion without dates/LADs per section' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

const ruleJCTScheduleOfAmendmentsImpact: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /schedule\s+of\s+amendments|amendments\s+to\s+jct/i.test(text);
  if (mentions) {
    const sensitive = /(payment\s+notice|pay\s+less\s+notice|final\s+date\s+for\s+payment|relevant\s+events|loss\s+and\s+expense|relevant\s+matters)/i.test(text);
    if (sensitive) {
      const idx = text.search(/schedule\s+of\s+amendments|amendments\s+to\s+jct/i);
      const s = Math.max(0, idx - 200); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 300);
      findings.push({
        id: 'jct-schedule-amendments-impact',
        category: 'Other',
        severity: 'high',
        actionType: 'cta',
        title: 'Schedule of Amendments may alter core mechanisms',
        summary: 'The Schedule of Amendments appears to modify payment, EOT, or loss & expense provisions. Ensure the changes are intentional and clear.',
        recommendation: 'Review Schedule of Amendments changes to payment notices, pay less notices, Relevant Events/Matters, and timelines for clarity and compliance.',
        clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.5, rationale: 'Amendments mention near sensitive mechanisms' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

const ruleJCTVariationsValuation: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /variation(s)?|instruction(s)?\s+to\s+vary/i.test(text);
  if (mentions) {
    const valuation = /(valuation|rates\s+and\s+prices|daywork|schedule\s+of\s+rates)/i.test(text);
    if (!valuation) {
      const idx = text.search(/variation|vary/i);
      const s = Math.max(0, idx - 160); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 260);
      findings.push({
        id: 'jct-variations-valuation-unclear',
        category: 'Other',
        severity: 'medium',
        actionType: 'cta',
        title: 'Variations valuation basis unclear',
        summary: 'Variations are referenced but the valuation method (rates/prices, new rates, daywork) is not clearly stated.',
        recommendation: 'State the valuation basis for variations (existing rates, new rates agreement, or daywork) and records required.',
        clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.45, rationale: 'Variation mention without valuation tokens' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

// =====================
// JCT Rule Pack (UK construction)
// =====================

const ruleJCTPaymentMechanics: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /(due\s+date|final\s+date\s+for\s+payment|payment\s+notice|pay\s+less\s+notice|interim\s+valuation)/i.test(text);
  if (!mentions) {
    const snippet = text.slice(0, Math.min(260, text.length));
    findings.push({
      id: 'jct-payment-mechanics-weak',
      category: 'PaymentTerms',
      severity: 'high',
      actionType: 'cta',
      title: 'Payment mechanics unclear (due date/final date/pay less notices)',
      summary: 'JCT contracts typically set due date, final date, payment notice, pay less notice, and interest. These are not clearly detected.',
      recommendation: 'Add or clarify due date and final date for payment, payment notice and pay less notice steps, and late payment interest.',
      clauseSpan: { text: snippet, startIndex: 0, endIndex: snippet.length },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.55, rationale: 'Heuristic absence of key payment tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// JCT: Interim and final account mechanics clarity (missing definition added)
const ruleJCTInterimFinalAccounts: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const interim = /(interim\s+valuation|interim\s+application|stage\s+payment)/i.test(text);
  const finalAcc = /(final\s+account|final\s+certificate|final\s+payment)/i.test(text);
  const dueFinalLink = /(final\s+date\s+for\s+payment|due\s+date)/i.test(text);
  if (!interim || !finalAcc || !dueFinalLink) {
    const idx = text.search(/interim|final\s+account|certificate/i);
    const s = Math.max(0, idx - 200); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 300);
    findings.push({
      id: 'jct-accounts-mechanics-weak',
      category: 'PaymentTerms',
      severity: 'medium',
      actionType: 'cta',
      title: 'Interim/final account mechanics unclear',
      summary: 'Interim valuations/applications or final account mechanics and due/final dates are not clearly stated.',
      recommendation: 'Clarify interim valuation/application process, and explicitly set due date/final date for payments including the final certificate.',
      clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.45, rationale: 'Missing tokens for interim/final mechanics' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleJCTEOTRelevantEvents: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const hasEOT = /(extension\s+of\s+time|eot|relevant\s+events)/i.test(text);
  if (!hasEOT) {
    const idx = text.search(/time|relevant\s+events/i);
    const s = Math.max(0, idx - 160); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 260);
    findings.push({
      id: 'jct-eot-missing',
      category: 'Other',
      severity: 'high',
      actionType: 'cta',
      title: 'Extensions of Time / Relevant Events not detected',
      summary: 'JCT EOT mechanism and list of Relevant Events not clearly detected. This affects delay entitlement.',
      recommendation: 'Include EOT provisions and list of Relevant Events with notice and assessment steps.',
      clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.5, rationale: 'Absence of EOT/Relevant Events tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleJCTLiquidatedDamages: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /(liquidated\s+damages|LADs?|delay\s+damages)/i.test(text);
  if (mentions) {
    const rateMention = /(per\s+day|per\s+week|per\s+month|rate\s+of\s+|£|\$|\d+%)/i.test(text);
    if (!rateMention) {
      const idx = text.search(/(liquidated\s+damages|delay\s+damages)/i);
      const s = Math.max(0, idx - 140); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
      findings.push({
        id: 'jct-lads-rate-unclear',
        category: 'PaymentTerms',
        severity: 'medium',
        actionType: 'cta',
        title: 'Liquidated damages rate unclear',
        summary: 'LADs are referenced but the rate or calculation basis is unclear.',
        recommendation: 'Specify LADs rate, accrual basis, any caps, and concurrency stance if applicable.',
        clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.45, rationale: 'LADs mention without rate tokens' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

const ruleJCTLossAndExpense: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /(loss\s+and\s+expense|relevant\s+matters)/i.test(text);
  if (!mentions) {
    const idx = text.search(/loss\s+and\s+expense|relevant\s+matters/i);
    const s = Math.max(0, idx - 160); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 260);
    findings.push({
      id: 'jct-loss-expense-missing',
      category: 'Other',
      severity: 'medium',
      actionType: 'cta',
      title: 'Loss and Expense / Relevant Matters not detected',
      summary: 'JCT often includes Loss and Expense recovery via Relevant Matters. This is not clearly detected.',
      recommendation: 'Include Loss and Expense provisions and list of Relevant Matters with notice/evidence requirements.',
      clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.45, rationale: 'Absence of L&E tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleJCTInsuranceOptions: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /(insurance\s+option\s+[abc]|option\s+[abc]\s+insurance|all\s+risks|public\s+liability|employers'?\s+liability|professional\s+indemnity)/i.test(text);
  if (mentions) {
    const hasLimits = /(£|\$|\d+\s*million|limit\s+of\s+indemnity)/i.test(text);
    if (!hasLimits) {
      const idx = text.search(/insurance/i);
      const s = Math.max(0, idx - 140); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
      findings.push({
        id: 'jct-insurance-limits-unclear',
        category: 'Other',
        severity: 'medium',
        actionType: 'cta',
        title: 'Insurance limits/responsibilities unclear',
        summary: 'Insurance options/cover are mentioned but limits or who arranges cover are not explicit.',
        recommendation: 'Specify insurance option (A/B/C), cover types, limits (e.g., £5m), and responsibility for arranging/evidencing cover.',
        clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.45, rationale: 'Insurance mention without limits/responsibility' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

const ruleJCTDesignResponsibility: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /(design\s+and\s+build|contractor\s+design|employer'?s\s+requirements|contractor'?s\s+proposals)/i.test(text);
  if (mentions) {
    const fitness = /(fitness\s+for\s+purpose)/i.test(text);
    if (fitness) {
      const idx = text.search(/fitness\s+for\s+purpose/i);
      const s = Math.max(0, idx - 140); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
      findings.push({
        id: 'jct-design-fitness-for-purpose-flag',
        category: 'Other',
        severity: 'medium',
        actionType: 'cta',
        title: 'Design responsibility may imply fitness for purpose',
        summary: 'JCT design responsibility typically avoids fitness for purpose; ensure negligence-standard obligations unless agreed.',
        recommendation: 'Qualify design obligations to a reasonable skill and care standard and avoid fitness for purpose unless intended.',
        clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.5, rationale: 'Fitness for purpose token in design section' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

const ruleJCTWarrantiesThirdPartyRights: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /(collateral\s+warranti(?:es|y)|third[-\s]?party\s+rights|step[-\s]?in)/i.test(text);
  if (!mentions) {
    const idx = text.search(/rights|warranti/i);
    const s = Math.max(0, idx - 160); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 260);
    findings.push({
      id: 'jct-warranties-tpr-missing',
      category: 'Other',
      severity: 'low',
      actionType: 'draft',
      title: 'Collateral warranties / third‑party rights not detected',
      summary: 'Projects often require collateral warranties or third‑party rights for funders/tenants with step‑in rights.',
      recommendation: 'Add collateral warranty or third‑party rights obligations and name required beneficiaries.',
      clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.35, rationale: 'No clear warranty/TPR tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleJCTProfessionalIndemnity: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /(professional\s+indemnity|pi\s+insurance)/i.test(text);
  if (mentions) {
    const hasLimit = /(£|\$|\d+\s*million|limit\s+of\s+indemnity)/i.test(text);
    if (!hasLimit) {
      const idx = text.search(/professional\s+indemnity|pi\s+insurance/i);
      const s = Math.max(0, idx - 140); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
      findings.push({
        id: 'jct-pi-limit-unclear',
        category: 'Other',
        severity: 'low',
        actionType: 'draft',
        title: 'Professional indemnity limit not clear',
        summary: 'PI insurance is referenced but the limit is not explicit; typical projects set £1m/£5m depending on scope.',
        recommendation: 'Specify PI limit and duration of cover (e.g., 6/12 years) with reasonable skill and care standard.',
        clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.35, rationale: 'PI mention without limits' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

const ruleJCTAdjudication: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /adjudication/i.test(text);
  if (mentions) {
    const hasTimeline = /(within\s+\d+\s*(?:days|weeks)|timetable)/i.test(text);
    if (!hasTimeline) {
      const idx = text.search(/adjudication/i);
      const s = Math.max(0, idx - 140); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
      findings.push({
        id: 'jct-adjudication-timing-weak',
        category: 'Other',
        severity: 'low',
        actionType: 'draft',
        title: 'Adjudication timetable not explicit',
        summary: 'Adjudication is referenced but response/decision timetable is unclear.',
        recommendation: 'Clarify adjudication timelines consistent with the Scheme where applicable.',
        clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.35, rationale: 'Adjudication mention without timetable tokens' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

const ruleJCTTermination: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'JCT') return findings;
  const mentions = /(termination|determination)/i.test(text);
  if (mentions) {
    const hasGrounds = /(insolvency|non[-\s]?payment|suspension|material\s+breach)/i.test(text);
    if (!hasGrounds) {
      const idx = text.search(/termination|determination/i);
      const s = Math.max(0, idx - 140); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
      findings.push({
        id: 'jct-termination-grounds-unclear',
        category: 'Termination',
        severity: 'medium',
        actionType: 'cta',
        title: 'Termination grounds not clear',
        summary: 'Termination/determination is referenced but grounds (e.g., insolvency, non-payment, material breach) are not clearly set out.',
        recommendation: 'List termination grounds and consequences clearly, aligning with JCT mechanisms.',
        clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.4, rationale: 'Termination mention without clear grounds' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

// Programme acceptance reasons list per traditional NEC3 wording
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ruleNEC3ProgrammeAcceptanceReasonsList: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const hasProgramme = /programme\b/i.test(text);
  if (!hasProgramme) return findings;
  // Look for explicit reasons the PM may not accept a programme
  const reasonsTokens = /does\s+not\s+show\s+the\s+order|does\s+not\s+comply|does\s+not\s+include|no\s+method\s+statements|resources\s+not\s+shown|not\s+practicable|does\s+not\s+allow\s+for|does\s+not\s+represent|does\s+not\s+take\s+into\s+account/i.test(text);
  if (!reasonsTokens) {
    const idx = text.search(/programme\b/i);
    const s = Math.max(0, idx - 160); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 260);
    findings.push({
      id: 'nec3-programme-acceptance-reasons-list-missing',
      category: 'Other',
      severity: 'low',
      actionType: 'draft',
      title: 'Programme acceptance reasons list not explicitly stated',
      summary: 'Traditional NEC3 lists reasons for non-acceptance (e.g., does not show order/timing, not practicable, does not comply, resources/methods missing). These are not explicitly detected.',
      recommendation: 'Add explicit acceptance reasons list (examples: does not show the order and timing of operations; is not practicable; does not comply with the contract; does not include required method statements/resources).',
      clauseSpan: { text: text.slice(s, e), startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.35, rationale: 'Heuristic absence of common acceptance reason tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// Float ownership interactions during CE assessment
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ruleNEC3FloatCEInteraction: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const ceAreaIdx = text.search(/compensation\s+events?/i);
  if (ceAreaIdx === -1) return findings;
  const s = Math.max(0, ceAreaIdx - 400); const e = Math.min(text.length, ceAreaIdx + 600);
  const ctx = text.slice(s, e);
  const mentionsFloat = /float/i.test(ctx);
  // If CE area does not mention float, flag potential ambiguity in assessments
  if (!mentionsFloat) {
    const prefOwner = config.preferences?.nec3FloatOwnership || 'Neutral';
    const prefTreatment = config.preferences?.nec3CEFloatTreatment || 'NotSpecified';
    const ownerPhrase = prefOwner === 'Contractor' ? 'contractor-owned float' : prefOwner === 'Employer' ? 'employer/client-owned float' : 'float ownership';
    let treatmentAdvice = 'Clarify how float is considered in CE assessments (e.g., ownership and whether float absorption affects time entitlement).';
    if (prefTreatment === 'IgnoreFloat') treatmentAdvice = 'Clarify that float should be ignored for CE time entitlement (no float absorption before granting time).';
    if (prefTreatment === 'UseFloat') treatmentAdvice = 'Clarify that available float is used before granting additional time for CEs.';
    findings.push({
      id: 'nec3-float-ce-interaction-unclear',
      category: 'Other',
      severity: 'low',
      actionType: 'draft',
      title: 'Float interaction during CE assessments is not explicit',
      summary: `Compensation Event assessment area does not reference Programme ${ownerPhrase}/usage, which can affect entitlement and delay analysis.`,
      recommendation: treatmentAdvice,
      clauseSpan: { text: ctx, startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.35, rationale: 'CE context without float tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// Z-clause variations affecting CE quotation period defaults
const ruleNEC3ZClauseQuotationVariations: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const zMention = /\bZ\s*\d+\b|Z\s+clauses?/i.test(text);
  const quotesMention = /quotation|quotations|quote\b/i.test(text);
  if (!zMention || !quotesMention) return findings;
  // Search for explicit duration near quotation mentions
  const idx = text.search(/quotation|quotations|quote\b/i);
  const s = Math.max(0, idx - 220); const e = Math.min(text.length, (idx === -1 ? 0 : idx) + 320);
  const window = text.slice(s, e);
  const hasDuration = /within\s+\d+\s*(?:days|weeks)|\d+\s*(?:day|week)\s+period/i.test(window);
  if (!hasDuration) {
    findings.push({
      id: 'nec3-z-quotation-period-unclear',
      category: 'Other',
      severity: 'medium',
      actionType: 'cta',
      title: 'Z clause may alter quotation periods without explicit duration',
      summary: 'Z clauses are referenced near quotation process but no explicit submission duration detected. Defaults may be overridden without clarity.',
      recommendation: 'Specify the quotation submission period explicitly when Z clauses vary or override default timescales.',
      clauseSpan: { text: window, startIndex: s, endIndex: e },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.4, rationale: 'Z clause + quotation without explicit time window' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// =====================
// NEC3 Rule Pack
// =====================

const ruleNEC3EarlyWarning: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const mentionsEW = /early\s+warning/i.test(text);
  const hasTimeframe = /(within\s+\d+\s*(?:days|weeks)|promptly|as\s+soon\s+as\s+practicable)/i.test(text);
  if (mentionsEW && !hasTimeframe) {
    const idx = text.search(/early\s+warning/i);
    const start = Math.max(0, idx - 140); const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
    findings.push({
      id: 'nec3-ew-timeframe-missing',
      category: 'Other',
      severity: 'medium',
      actionType: 'cta',
      title: 'NEC3 Early Warning lacks clear timeframe/obligations',
      summary: 'Early Warning mechanism is mentioned but notification timeframe or specific obligations are unclear.',
      recommendation: 'Specify prompt notification timeframe and responsibilities for Early Warnings with Risk Reduction meetings.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.5, rationale: 'EW mention without timeframe keywords' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleNEC3CompensationEvents: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const mentionsCE = /compensation\s+events?/i.test(text);
  if (!mentionsCE) return findings;
  const hasNoticeWindow = /(within\s+\d+\s*(?:days|weeks)\s+of\s+(?:becoming\s+aware|the\s+event))/i.test(text);
  const hasAssessment = /(assessment|quotations?|project\s+manager\s+assesses)/i.test(text);
  if (!hasNoticeWindow || !hasAssessment) {
    const idx = text.search(/compensation\s+events?/i);
    const start = Math.max(0, idx - 140); const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 240);
    findings.push({
      id: 'nec3-ce-weak',
      category: 'Other',
      severity: 'high',
      actionType: 'cta',
      title: 'NEC3 Compensation Events: notice window/assessment unclear',
      summary: 'Compensation Events are referenced but notification windows or assessment methods are unclear or missing.',
      recommendation: 'Clarify notification timeframe (e.g., within X days of awareness) and assessment/quotation procedures.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.55, rationale: 'CE mention without clear windows/assessment' },
      createdAt: new Date().toISOString(),
    });
  }

  // Specific time-bar: "within 8 weeks of becoming aware" (or similar) near CE mentions
  const eightWeekPattern = /(within\s+8\s*weeks\s+of\s+(?:becoming\s+aware|the\s+event))/i;
  const ceWindowContextIdx = text.search(/compensation\s+events?/i);
  if (ceWindowContextIdx !== -1) {
    const s = Math.max(0, ceWindowContextIdx - 300);
    const e = Math.min(text.length, ceWindowContextIdx + 400);
    const windowCtx = text.slice(s, e);
    if (!eightWeekPattern.test(windowCtx)) {
      findings.push({
        id: 'nec3-ce-timebar-8weeks-missing',
        category: 'Other',
        severity: 'medium',
        actionType: 'cta',
        title: 'Compensation Event time-bar (8 weeks) not detected',
        summary: 'Typical NEC3 time-bar like "within 8 weeks of becoming aware" is not detected near Compensation Event clauses.',
        recommendation: 'Confirm CE notification time-bar (e.g., 8 weeks from awareness) to avoid disputes over entitlement.',
        clauseSpan: { text: windowCtx, startIndex: s, endIndex: e },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.45, rationale: 'Heuristic absence of 8-week window near CE' },
        createdAt: new Date().toISOString(),
      });
    }
  }

  // Quotation/assessment deadlines clarity
  const quoteDeadline = /(submit\s+quotations?\s+within\s+\d+\s*(?:days|weeks)|quotation\s+period)/i.test(text);
  const pmAssessDeadline = /(project\s+manager\s+assesses.*within\s+\d+\s*(?:days|weeks))/i.test(text);
  if (!quoteDeadline || !pmAssessDeadline) {
    const idx2 = text.search(/quotations?|assesses/i);
    const s2 = Math.max(0, idx2 - 180); const e2 = Math.min(text.length, (idx2 === -1 ? 0 : idx2) + 260);
    findings.push({
      id: 'nec3-ce-quotation-assessment-deadlines-weak',
      category: 'Other',
      severity: 'medium',
      actionType: 'cta',
      title: 'Compensation Event quotation/assessment deadlines unclear',
      summary: 'Quotation submission or Project Manager assessment deadlines for CEs are unclear or absent.',
      recommendation: 'Define clear quotation submission period (e.g., within X days) and PM assessment window.',
      clauseSpan: { text: text.slice(s2, e2), startIndex: s2, endIndex: e2 },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.45, rationale: 'Heuristic: missing explicit deadlines' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleNEC3Programme: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const mentionsProgramme = /programme\b/i.test(text);
  if (!mentionsProgramme) return findings;
  const updateCadence = /(update|revise|revised)\s+programme/i.test(text);
  const acceptance = /(acceptance|accepted\s+by\s+the\s+project\s+manager)/i.test(text);
  if (!updateCadence || !acceptance) {
    const idx = text.search(/programme\b/i);
    const start = Math.max(0, idx - 140); const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 240);
    findings.push({
      id: 'nec3-programme-weak',
      category: 'Other',
      severity: 'medium',
      actionType: 'cta',
      title: 'NEC3 Programme update/acceptance unclear',
      summary: 'Programme provisions lack clear update cadence or acceptance criteria by the Project Manager.',
      recommendation: 'Define revision cadence and acceptance process, including reasons for non-acceptance and resubmission timing.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.45, rationale: 'Programme mention without update/acceptance keywords' },
      createdAt: new Date().toISOString(),
    });
  }

  // Float ownership detection (often contentious). Look for explicit ownership or ambiguity.
  const floatTokens = /(float\s+(?:is\s+owned\s+by|belongs\s+to)\s+(?:the\s+contractor|the\s+employer|the\s+client))/i.test(text);
  if (!floatTokens) {
    const idx3 = text.search(/programme\b/i);
    const s3 = Math.max(0, idx3 - 160); const e3 = Math.min(text.length, (idx3 === -1 ? 0 : idx3) + 260);
    findings.push({
      id: 'nec3-programme-float-ownership-unclear',
      category: 'Other',
      severity: 'low',
      actionType: 'draft',
      title: 'Programme float ownership not clearly stated',
      summary: 'No explicit statement detected on who owns the float in the Programme, which can affect change assessments.',
      recommendation: 'Clarify float ownership (e.g., contractor-owned) to reduce contention during CE assessments.',
      clauseSpan: { text: text.slice(s3, e3), startIndex: s3, endIndex: e3 },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.35, rationale: 'Heuristic absence of float ownership tokens' },
      createdAt: new Date().toISOString(),
    });
  }

  // PM acceptance reasons explicitly stated
  const acceptanceReasons = /(does\s+not\s+show\s+the\s+order|does\s+not\s+comply|unacceptable\s+for\s+the\s+following\s+reasons)/i.test(text);
  if (!acceptanceReasons) {
    const idx4 = text.search(/acceptance|accepted\s+by\s+the\s+project\s+manager/i);
    const s4 = Math.max(0, idx4 - 160); const e4 = Math.min(text.length, (idx4 === -1 ? 0 : idx4) + 260);
    findings.push({
      id: 'nec3-programme-acceptance-reasons-unclear',
      category: 'Other',
      severity: 'low',
      actionType: 'draft',
      title: 'PM acceptance reasons not explicit',
      summary: 'Programme acceptance criteria/reasons for non-acceptance by the Project Manager are not explicitly stated.',
      recommendation: 'List acceptance criteria and reasons for withholding acceptance to avoid ambiguity.',
      clauseSpan: { text: text.slice(s4, e4), startIndex: s4, endIndex: e4 },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.35, rationale: 'Heuristic absence of acceptance reason tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleNEC3ConstructionActPayment: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const mentionsPayment = /(payment\s+notice|pay\s+less\s+notice|due\s+date|final\s+date\s+for\s+payment)/i.test(text);
  if (!mentionsPayment) {
    const snippet = text.slice(0, Math.min(260, text.length));
    findings.push({
      id: 'nec3-construction-act-weak',
      category: 'PaymentTerms',
      severity: 'high',
      actionType: 'cta',
      title: 'Construction Act payment mechanics unclear',
      summary: 'Payment terms may lack due date, payment notice, pay less notice, or interest provisions compliant with the Construction Act.',
      recommendation: 'Add due date/final date, payment notice and pay less notice processes, and statutory interest/late payment clauses.',
      clauseSpan: { text: snippet, startIndex: 0, endIndex: snippet.length },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.5, rationale: 'Heuristic: missing key payment keywords' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleNEC3DelayDamages: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const mentionsLD = /(liquidated\s+damages|delay\s+damages)/i.test(text);
  if (mentionsLD) {
    const rateMention = /(per\s+day|per\s+week|per\s+month|rate\s+of\s+|£|\$|\d+%)/i.test(text);
    if (!rateMention) {
      const idx = text.search(/(liquidated\s+damages|delay\s+damages)/i);
      const start = Math.max(0, idx - 140); const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
      findings.push({
        id: 'nec3-ld-rate-unclear',
        category: 'PaymentTerms',
        severity: 'medium',
        actionType: 'cta',
        title: 'Delay/Liquidated damages rate unclear',
        summary: 'Delay damages are referenced but the rate or basis of calculation appears unclear.',
        recommendation: 'Specify the damages rate, accrual basis, caps, and any concurrency or relief events interaction.',
        clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.45, rationale: 'LD mention without rate tokens' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

const ruleNEC3Retention: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const mentionsRetention = /retention\b/i.test(text);
  if (mentionsRetention) {
    const hasRate = /(\d+\s*%|percent)/i.test(text);
    const releaseClarity = /(practical\s+completion|defects\s+date|release)/i.test(text);
    if (!hasRate || !releaseClarity) {
      const idx = text.search(/retention\b/i);
      const start = Math.max(0, idx - 140); const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
      findings.push({
        id: 'nec3-retention-unclear',
        category: 'PaymentTerms',
        severity: 'medium',
        actionType: 'cta',
        title: 'Retention rate/release timing unclear',
        summary: 'Retention is referenced but the rate or release triggers (e.g., PC/defects date) are unclear.',
        recommendation: 'Define retention percentage and the specific release milestones with any partial release percentages.',
        clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.45, rationale: 'Retention mention without rate/release tokens' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

const ruleNEC3RiskRegister: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const mentionsRR = /risk\s+register/i.test(text);
  const hasMaintenance = /(maintain|update|review)/i.test(text);
  if (mentionsRR && !hasMaintenance) {
    const idx = text.search(/risk\s+register/i);
    const start = Math.max(0, idx - 140); const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
    findings.push({
      id: 'nec3-risk-register-weak',
      category: 'Other',
      severity: 'low',
      actionType: 'draft',
      title: 'Risk Register maintenance not specified',
      summary: 'Risk Register is mentioned but maintenance/review cadence is unclear.',
      recommendation: 'Add obligations to maintain and review the Risk Register (e.g., in Risk Reduction meetings).',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.35, rationale: 'RR mention without maintenance tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleNEC3Adjudication: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const mentionsAdj = /adjudication/i.test(text);
  if (!mentionsAdj) return findings;
  const hasTiming = /(within\s+\d+\s*(?:days|weeks)|timetable)/i.test(text);
  if (!hasTiming) {
    const idx = text.search(/adjudication/i);
    const start = Math.max(0, idx - 140); const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
    findings.push({
      id: 'nec3-adjudication-timing-weak',
      category: 'Other',
      severity: 'low',
      actionType: 'draft',
      title: 'Adjudication process lacks clear timetable',
      summary: 'Adjudication is referenced but response/decision timetable appears unclear.',
      recommendation: 'Define adjudication timelines consistent with statutory or scheme requirements.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.35, rationale: 'Adj mention without timetable tokens' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleNEC3Insurance: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NEC3') return findings;
  const mentionsIns = /insurance|insurer|public\s+liability|professional\s+indemnity|employers'?\s+liability/i.test(text);
  if (mentionsIns) {
    const hasLimits = /(£|\$|\d+\s*million|limit\s+of\s+indemnity)/i.test(text);
    const responsibility = /(contractor\s+shall\s+maintain|who\s+arranges)/i.test(text);
    if (!hasLimits || !responsibility) {
      const idx = text.search(/insurance/i);
      const start = Math.max(0, idx - 140); const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
      findings.push({
        id: 'nec3-insurance-weak',
        category: 'Other',
        severity: 'medium',
        actionType: 'cta',
        title: 'Insurance limits/responsibilities unclear',
        summary: 'Insurance provisions lack clear limits or responsibility for arranging cover.',
        recommendation: 'Specify cover types (PL/PI/EL), limits (e.g., £5m), and responsibility for arranging and evidencing cover.',
        clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.45, rationale: 'Insurance mention without limits/responsibility tokens' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

// NDA: Mutuality check (one-sided confidentiality obligations)
const ruleNDAMutuality: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  const isNDA = config.contractType === 'NDA';
  if (!isNDA) return findings;
  const mentionsConf = /(confidential|confidentiality|non[-\s]?disclosure)/i.test(text);
  if (!mentionsConf) return findings;
  // Heuristic: obligations on "Recipient" only without reciprocal language
  const oneSided = /the\s+recipient\s+shall\s+(?:not\s+)?disclose/i.test(text) && !/each\s+party\s+shall|both\s+parties\s+shall|mutual\s+non[-\s]?disclosure/i.test(text);
  if (oneSided) {
    const idx = text.search(/recipient\s+shall/i);
    const start = Math.max(0, idx - 140);
    const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 200);
    findings.push({
      id: 'nda-mutuality',
      category: 'Confidentiality',
      severity: 'medium',
      actionType: 'cta',
      title: 'NDA appears one-sided (lack of mutuality)',
      summary: 'Confidentiality obligations seem to bind only one party. Mutual obligations are common in NDAs.',
      recommendation: 'Make confidentiality obligations mutual, with symmetrical definitions, exceptions, and remedies.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.45, rationale: 'Heuristic: Recipient-only obligation detected' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// NDA: Residual knowledge carve-out presence
const ruleNDAResidualKnowledge: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NDA') return findings;
  const mentionsResiduals = /(residual\s+knowledge|unaided\s+memory|residuals\s+clause)/i.test(text);
  const veryStrict = /(recipient\s+shall\s+not\s+use\s+or\s+disclose\s+any\s+information\s+whatsoever)/i.test(text);
  if (!mentionsResiduals && veryStrict) {
    const snippet = text.slice(0, Math.min(260, text.length));
    findings.push({
      id: 'nda-residuals-missing',
      category: 'Confidentiality',
      severity: 'low',
      actionType: 'draft',
      title: 'Residual knowledge carve-out not detected',
      summary: 'NDA does not appear to include a residual knowledge carve-out, which some parties require for practicality.',
      recommendation: 'Consider a residual knowledge carve-out narrowly tailored to protect trade secrets and compliance obligations.',
      clauseSpan: { text: snippet, startIndex: 0, endIndex: snippet.length },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.35, rationale: 'Heuristic absence with strict language' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// NDA: Injunctive relief availability
const ruleNDAInjunctiveRelief: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  if (config.contractType !== 'NDA') return findings;
  const mentionsInjunction = /(injunctive\s+relief|equitable\s+relief|specific\s+performance)/i.test(text);
  if (!mentionsInjunction) {
    const snippet = text.slice(0, Math.min(240, text.length));
    findings.push({
      id: 'nda-injunctive-missing',
      category: 'Confidentiality',
      severity: 'low',
      actionType: 'draft',
      title: 'No injunctive relief clause detected',
      summary: 'NDAs often include equitable/injunctive relief language to address irreparable harm from breach.',
      recommendation: 'Add injunctive relief wording affirming the right to seek equitable remedies in addition to damages.',
      clauseSpan: { text: snippet, startIndex: 0, endIndex: snippet.length },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.3, rationale: 'Heuristic absence' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// SaaS/MSA: Uptime commitment and service credits
const ruleSaaSUptimeCredits: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  const isSaaS = config.contractType === 'SaaS' || config.contractType === 'MSA';
  if (!isSaaS) return findings;
  const mentionsUptime = /(uptime|availability|service\s+level|sla)/i.test(text);
  const mentionsCredits = /(service\s+credits?|credit\s+\d+%|credit\s+against\s+fees)/i.test(text);
  if (mentionsUptime && !mentionsCredits) {
    const idx = text.search(/(uptime|availability|service\s+level|sla)/i);
    const start = Math.max(0, idx - 140);
    const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
    findings.push({
      id: 'saas-uptime-credits-missing',
      category: 'SLA',
      severity: 'medium',
      actionType: 'cta',
      title: 'SLA uptime without clear service credits',
      summary: 'Uptime/availability is mentioned but there are no explicit service credits for breach.',
      recommendation: 'Define measurable SLAs (e.g., 99.9%) and a clear service credit schedule, with exclusions and claim process.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.45, rationale: 'Heuristic: uptime without credits' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// SaaS/MSA: Data portability and retention after termination
const ruleSaaSDataPortabilityRetention: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  const isSaaS = config.contractType === 'SaaS' || config.contractType === 'MSA';
  if (!isSaaS) return findings;
  const mentionsExport = /(export|portability|download|extract)\s+(?:data|customer\s+data)/i.test(text);
  const mentionsRetention = /(retain|retention|deletion|delete|destroy)\s+(?:data|customer\s+data)/i.test(text);
  if (!mentionsExport || !mentionsRetention) {
    const snippet = text.slice(0, Math.min(260, text.length));
    findings.push({
      id: 'saas-portability-retention-weak',
      category: 'DataProtection',
      severity: 'medium',
      actionType: 'cta',
      title: 'Missing or weak data portability/retention terms',
      summary: 'Contracts should specify data export format, window after termination, and deletion timeline/verification.',
      recommendation: 'Add clear post-termination data export (format and window) and deletion/retention commitments with certification.',
      clauseSpan: { text: snippet, startIndex: 0, endIndex: snippet.length },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.4, rationale: 'Heuristic: missing explicit portability/retention keywords' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// UK UCTA/CRA context: exclusion of liability for death/personal injury is generally unenforceable
const ruleUKDeathInjuryLiability: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  const isUK = (config.primaryJurisdiction || '').toUpperCase() === 'UK';
  if (!isUK) return findings;
  const regex = /(exclude|limit)\s+(?:our|the\s+company'?s|provider'?s)?\s*liabilit\w+[^\n]{0,60}(death|personal\s+injur\w+)/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    const start = Math.max(0, m.index - 140);
    const end = Math.min(text.length, m.index + 200);
    findings.push({
      id: `uk-death-injury-${m.index}`,
      category: 'Liability',
      severity: 'critical',
      actionType: 'cta',
      title: 'Unenforceable exclusion of liability (death/personal injury)',
      summary: 'Detected clause attempting to exclude or limit liability for death or personal injury under UK law, typically unenforceable.',
      recommendation: 'Remove exclusion/limitation for death or personal injury and ensure liability wording aligns with UK UCTA/CRA standards.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.7, rationale: 'Regex proximity between exclusion/limit and death/personal injury' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// GDPR/Data Processing: detect personal data references without DPA terms
const ruleGDPRDPARequired: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  // If mentions personal data/processing but lacks DPA signals
  const mentionsPersonalData = /(personal\s+data|data\s+subject|processor|controller|gdpr)/i.test(text);
  if (!mentionsPersonalData) return findings;
  const hasDPAKeywords = /(data\s+processing\s+agreement|dpa\b|controller\s+and\s+processor\s+obligations|article\s+28\b|sub-?processor|record\s+of\s+processing)/i.test(text);
  if (!hasDPAKeywords) {
    const snippet = text.slice(0, Math.min(300, text.length));
    findings.push({
      id: 'gdpr-dpa-missing',
      category: 'DataProtection',
      severity: 'high',
      actionType: 'cta',
      title: 'Possible missing Data Processing Agreement (DPA) terms',
      summary: 'Contract references personal data but lacks clear DPA obligations (roles, Article 28 terms, subprocessors).',
      recommendation: 'Add a DPA covering roles (controller/processor), Article 28 obligations, subprocessor approvals, and security measures.',
      clauseSpan: { text: snippet, startIndex: 0, endIndex: snippet.length },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.5, rationale: 'Heuristic: personal data mentioned but no DPA keywords' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// Governing law/venue asymmetry: one-sided jurisdiction/forum selection
const ruleGoverningLawVenueAsymmetry: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  const regex = /(exclusive\s+jurisdiction\s+of\s+[^\n]+|governing\s+law\s+of\s+[^\n]+)/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    const start = Math.max(0, m.index - 140);
    const end = Math.min(text.length, m.index + 200);
    const snippet = text.slice(start, end);
    const looksOneSided = /(customer|client|you)\s+consent\s+to\s+the\s+exclusive\s+jurisdiction/i.test(snippet) && !/(provider|we)\s+consent\s+to/i.test(snippet);
    if (looksOneSided) {
      findings.push({
        id: `law-venue-asym-${m.index}`,
        category: 'GoverningLaw',
        severity: 'medium',
        actionType: 'cta',
        title: 'One-sided governing law or venue',
        summary: 'Jurisdiction/venue appears one-sided in favor of provider. Consider mutual or neutral venue.',
        recommendation: 'Negotiate mutual governing law/venue or a neutral forum; add carve-outs for injunctive relief if needed.',
        clauseSpan: { text: snippet, startIndex: start, endIndex: end },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.45, rationale: 'Heuristic one-sided phrasing' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

// IP Ownership ambiguity in deliverables/work product
const ruleIPOwnershipAmbiguity: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  const hasDeliverables = /(deliverable|work\s*product|outputs|materials)/i.test(text);
  const mentionsOwnership = /(own(er(ship)?)?|assign|license\s+to|retain\s+all\s+rights)/i.test(text);
  if (hasDeliverables && !mentionsOwnership) {
    const snippet = text.slice(0, Math.min(280, text.length));
    findings.push({
      id: 'ip-ownership-ambiguous',
      category: 'IP',
      severity: 'medium',
      actionType: 'cta',
      title: 'Deliverables IP ownership not clearly defined',
      summary: 'Work product/deliverables referenced but ownership or license terms are unclear.',
      recommendation: 'Specify who owns the deliverables, what pre-existing IP remains with each party, and license scope if applicable.',
      clauseSpan: { text: snippet, startIndex: 0, endIndex: snippet.length },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.4, rationale: 'Heuristic: deliverables without ownership terms' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

// Cross-border data transfers without safeguards (EU/UK)
const ruleCrossBorderDataTransfers: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  const isEUorUK = /^(EU|UK)$/i.test(String(config.primaryJurisdiction));
  if (!isEUorUK) return findings;
  const mentionsXfer = /(transfer|transferred|store|stored)\s+(?:outside|outside\s+of|outside\s+the)\s+(?:eea|eu|united\s+kingdom|uk)/i.test(text);
  const hasSafeguards = /(standard\s+contractual\s+clauses|sccs|uk\s+addendum|adequacy\s+decision|binding\s+corporate\s+rules)/i.test(text);
  if (mentionsXfer && !hasSafeguards) {
    const idx = text.search(/(transfer|store)[^\n]{0,120}(eea|eu|uk)/i);
    const start = Math.max(0, idx - 140);
    const end = Math.min(text.length, (idx === -1 ? 0 : idx) + 220);
    findings.push({
      id: 'xfer-safeguards-missing',
      category: 'DataProtection',
      severity: 'high',
      actionType: 'cta',
      title: 'Cross-border data transfers without safeguards',
      summary: 'Mentions transferring/storing personal data outside EU/UK but lacks SCCs, UK Addendum, or other safeguards.',
      recommendation: 'Add appropriate transfer mechanisms (SCCs + UK Addendum where relevant) and document transfer impact assessments if required.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.5, rationale: 'Heuristic mention of cross-border without safeguards keywords' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleAutoRenewal: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  const regex = /(auto[-\s]?renew|automatically\s+renew|renewal\s+term)/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const start = Math.max(0, match.index - 120);
    const end = Math.min(text.length, match.index + 140);
    findings.push({
      id: `auto-renew-${match.index}`,
      category: 'AutoRenewal',
      severity: 'medium',
      actionType: 'cta',
      title: 'Automatic renewal detected',
      summary: 'Auto-renewal may be acceptable but often requires clear notice and termination windows.',
      recommendation: 'Add prominent notice obligations (e.g., 30-60 days) and allow termination for convenience at renewal.',
      clauseSpan: {
        text: text.slice(start, end),
        startIndex: start,
        endIndex: end,
      },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.55, rationale: 'Regex keyword match' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

export function runLocalRules(text: string, config: AnalyzerConfig): Finding[] {
  if (!text || !text.trim()) return [];
  const ctx: RuleContext = { text, config };
  const rules: RuleFn[] = [
    ruleSoleDiscretion,
    ruleAutoRenewal,
    ruleUncappedLiability,
    ruleIndemnityAsymmetry,
    ruleAssignmentRestrictions,
    ruleTerminationNotice,
    ruleUKDeathInjuryLiability,
    ruleGDPRDPARequired,
    ruleGoverningLawVenueAsymmetry,
    ruleIPOwnershipAmbiguity,
    ruleCrossBorderDataTransfers
  ];
  // Contract-type specific checks
  if (config.contractType === 'NDA') {
    rules.push(ruleNDAMutuality, ruleNDAResidualKnowledge, ruleNDAInjunctiveRelief);
  }
  if (config.contractType === 'SaaS' || config.contractType === 'MSA') {
    rules.push(ruleSaaSUptimeCredits, ruleSaaSDataPortabilityRetention);
  }
  if (config.contractType === 'NEC3') {
    rules.push(
      ruleNEC3EarlyWarning,
      ruleNEC3CompensationEvents,
      ruleNEC3CEPathway,
      ruleNEC3Programme,
      ruleNEC3ProgrammeReplyCadence,
      ruleNEC3ConstructionActPayment,
      ruleNEC3ConstructionActRights,
      ruleNEC3DelayDamages,
      ruleNEC3Retention,
      ruleNEC3RiskRegister,
      ruleNEC3Adjudication,
      ruleNEC3Insurance,
      ruleNEC3ZClauseQuotationVariations
    );
  }
  if (config.contractType === 'JCT') {
    rules.push(
      ruleJCTPaymentMechanics,
      ruleJCTInterimFinalAccounts,
      ruleJCTEOTRelevantEvents,
      ruleJCTLiquidatedDamages,
      ruleJCTLossAndExpense,
      ruleJCTInsuranceOptions,
      ruleJCTDesignResponsibility,
      ruleJCTWarrantiesThirdPartyRights,
      ruleJCTProfessionalIndemnity,
      ruleJCTAdjudication,
      ruleJCTTermination,
      ruleJCTSectionalCompletion,
      ruleJCTScheduleOfAmendmentsImpact,
      ruleJCTVariationsValuation,
      ruleJCTLADsCapsConcurrency
    );
  }
  return rules.flatMap((r) => r(ctx));
}

const ruleUncappedLiability: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  // Look for phrases suggesting unlimited liability or absence of cap terms near liability
  const regex = /(unlimited\s+liab|limit of liability\s*(?:shall\s*not\s*apply|does\s*not\s*apply)|without\s+limit\s+of\s+liability)/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const start = Math.max(0, match.index - 150);
    const end = Math.min(text.length, match.index + 180);
    findings.push({
      id: `uncapped-liability-${match.index}`,
      category: 'Liability',
      severity: 'high',
      actionType: 'cta',
      title: 'Potential uncapped liability',
      summary: 'Detected language implying unlimited liability or exceptions that negate caps.',
      recommendation: 'Negotiate a reasonable aggregate cap (e.g., 12 months fees) and clearly define excluded categories.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.55, rationale: 'Regex keyword match near liability' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleIndemnityAsymmetry: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  // Heuristic: mentions of indemnify/hold harmless coupled with only one party name may indicate asymmetry
  const regex = /(indemnif\w+|hold\s+harmless)/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const start = Math.max(0, match.index - 160);
    const end = Math.min(text.length, match.index + 200);
    const snippet = text.slice(start, end);
    // simple heuristic: presence of phrases like "customer shall indemnify" without reciprocal
    const looksOneSided = /customer\s+shall\s+indemnify|client\s+shall\s+indemnify|you\s+shall\s+indemnify/i.test(snippet) && !/provider\s+shall\s+indemnify|we\s+shall\s+indemnify/i.test(snippet);
    if (looksOneSided) {
      findings.push({
        id: `indemnity-asym-${match.index}`,
        category: 'Indemnity',
        severity: 'high',
        actionType: 'cta',
        title: 'Potential asymmetric indemnity',
        summary: 'Indemnity language appears one-sided. Consider reciprocal indemnities with appropriate scope.',
        recommendation: 'Add mutual indemnities for IP infringement, bodily injury, and property damage, with reasonable caps and procedures.',
        clauseSpan: { text: snippet, startIndex: start, endIndex: end },
        jurisdiction: config.primaryJurisdiction,
        contractType: config.contractType,
        confidence: { score: 0.45, rationale: 'Heuristic asymmetry detection' },
        createdAt: new Date().toISOString(),
      });
    }
  }
  return findings;
};

const ruleAssignmentRestrictions: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  const regex = /(no\s+assignment|shall\s+not\s+assign|may\s+not\s+assign|without\s+the\s+prior\s+written\s+consent\s+of)/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const start = Math.max(0, match.index - 140);
    const end = Math.min(text.length, match.index + 180);
    findings.push({
      id: `assignment-${match.index}`,
      category: 'Assignment',
      severity: 'medium',
      actionType: 'cta',
      title: 'Restrictive assignment/change-of-control language',
      summary: 'Assignment restrictions may block restructuring or sale. Ensure change-of-control exemptions and consent not unreasonably withheld.',
      recommendation: 'Insert change-of-control carve-out and allow assignment to affiliates with notice.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.5, rationale: 'Regex keyword match' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};

const ruleTerminationNotice: RuleFn = ({ text, config }) => {
  const findings: Finding[] = [];
  // Detect mention of notice windows like "within 10 days of renewal" etc.
  const regex = /(\b\d{1,2}\s*(?:day|days|business\s+days)\b[^\n]{0,40}\bnotice\b)|\btermination\b[^\n]{0,60}\bfor\s+convenience\b/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const start = Math.max(0, match.index - 150);
    const end = Math.min(text.length, match.index + 180);
    findings.push({
      id: `termination-${match.index}`,
      category: 'Termination',
      severity: 'medium',
      actionType: 'cta',
      title: 'Narrow termination window or missing convenience termination',
      summary: 'Termination terms may be too restrictive or lack termination-for-convenience, increasing lock-in risk.',
      recommendation: 'Negotiate reasonable notice periods (e.g., 30 days) and add termination-for-convenience with fair offboarding terms.',
      clauseSpan: { text: text.slice(start, end), startIndex: start, endIndex: end },
      jurisdiction: config.primaryJurisdiction,
      contractType: config.contractType,
      confidence: { score: 0.45, rationale: 'Regex pattern around notice/termination' },
      createdAt: new Date().toISOString(),
    });
  }
  return findings;
};
